import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatCurrency, formatKw } from '../../utils/format';
import { OrderItem } from './interfaces';

export const useRechargePowerInfo = (conectorStatus: any) => {
  const location = useLocation();
  const [showArticles, setShowArticles] = useState<boolean>();

  const checkRoute = useCallback(() => {
    const path = location.pathname;

    if (path.startsWith('/spott/extract/recharge/')) {
      setShowArticles(true);
      return true;
    }

    if (path.startsWith('/spott/recharge/')) {
      setShowArticles(false);
      return false;
    }
  }, [location.pathname]);

  const formatDecimalToTime = (decimalHour: number) => {
    if (decimalHour < 0) {
      return '00:00:00';
    }
    let totalSeconds = decimalHour * 3600;
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = Math.floor(totalSeconds % 60);
    return (
      String(hours).padStart(2, '0') +
      ':' +
      String(minutes).padStart(2, '0') +
      ':' +
      String(seconds).padStart(2, '0')
    );
  };

  const applyMappedDataFormatted = ({
    itemDesc,
    count,
    amount,
    value,
  }: {
    itemDesc: string;
    count: number;
    amount: number;
    value: number;
  }) => {
    const descItems = {
      SPOTT_EV_CHARGE_KWH: {
        title: 'Energia',
        count: `${formatKw(count)} kWh`,
        value: `${formatCurrency(value)} /kWh`,
      },
      SPOTT_EV_CHARGE_TIME_HOUR: {
        title: 'Carregador plugado',
        count: formatDecimalToTime(count < 0 ? 0.0 : count),
        value: `${formatCurrency(value < 0 ? 0.0 : value)} /h`,
      },
      SPOTT_EV_CHARGE_PRICE_TRANSACTION: {
        title: 'Taxa de serviço',
        count: '',
        value: '',
      },
      SPOTT_EV_CHARGE_TIME_HOUR_OVERSTAY: {
        title: 'Após recarga',
        count: formatDecimalToTime(count),
        value: `${formatCurrency(value)} /h`,
      },
      SPOTT_EV_CHARGE_TIME_HOUR_CHARGING: {
        title: 'Tempo de recarga',
        count: formatDecimalToTime(count),
        value: `${formatCurrency(value)} /h`,
      },
      SPOTT_DISCOUNTCOUPON_PERCENT: {
        title: 'Cupom aplicado',
        count: '',
        value: `${new Intl.NumberFormat('pt', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value * -100)}%`,
      },
      SPOTT_DISCOUNTCOUPON_PERCENT_ONLYABOVEVALUE: {
        title: 'Cupom aplicado',
        count: `Apenas acima de R$ ${new Intl.NumberFormat('pt', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(count)}`,
        value: `${new Intl.NumberFormat('pt', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value * -100)}%`,
      },
      SPOTT_DISCOUNTCOUPON_VALUE: {
        title: 'Cupom aplicado',
        count: '',
        value: `R$ ${new Intl.NumberFormat('pt', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount)}`,
      },
    } as const;

    return (
      descItems[itemDesc as keyof typeof descItems] || {
        title: 'unknown',
        count: 'unknown',
      }
    );
  };

  const mappedOrderItemsAvailable = (orderItemsAvailable: OrderItem[]) => {
    return orderItemsAvailable.map((item: OrderItem) => {
      const formattedData = applyMappedDataFormatted({
        itemDesc: item.orderItemDesc,
        count: item.orderItemCount,
        amount: item.orderItemAmount,
        value: item.orderItemPrice,
      });

      const formattedItem = {
        title: formattedData?.title,
        value: formattedData?.value,
        time: formattedData?.count,
        price: formatCurrency(item.orderItemAmount),
        refunded: item.orderItemRefunded,
      };

      return formattedItem;
    });
  };

  const renderInfoItems = () => {
    const orderItems = conectorStatus?.transaction?.order?.orderItems;

    const itemsToDisplay =
      orderItems?.length > 0 ? mappedOrderItemsAvailable(orderItems) : null;

    if (!itemsToDisplay || itemsToDisplay.length === 0) {
      return <p>Sem itens de cobrança</p>;
    }

    return itemsToDisplay.map((item: any, index: any) => (
      <article key={index} className='estimated-info-section__article'>
        <h3 className='estimated-info-section__title highlighted-text'>
          {item.title}
        </h3>
        <div
          className='estimated-info-section__info-row estimated-info-section__info-row--highlighted'
          style={item.refunded ? { textDecoration: 'line-through' } : {}}
        >
          <span>{item.value}</span>
          <span>{item.time}</span>
          <span>{item.price}</span>
        </div>
      </article>
    ));
  };

  useEffect(() => {
    checkRoute();
  }, [checkRoute]);

  return {
    showArticles,
    setShowArticles,
    renderInfoItems,
    formatCurrency,
  };
};
